import React, { useCallback, useRef, useState } from 'react'
import { IInstructionComponentInstance, IInstructionLookUpEntry, ITableColumnConfig } from 'src/types/report-designer'
import { IFormSchemaColumn } from 'src/generated-types/form-schema'
import { createNewColumn, applyFieldToDraft, parseDraftWidth, reorderColumns } from './TableComponentProperties.action'

export interface IUseInstructionComponentHandlersParams {
  instance: IInstructionComponentInstance
  onChange: (updated: IInstructionComponentInstance) => void
  columns?: IFormSchemaColumn[]
  onLookUpChange?: (
    updater: (prev: Record<string, IInstructionLookUpEntry>) => Record<string, IInstructionLookUpEntry>,
  ) => void
}

export const useInstructionComponentHandlers = ({
  instance,
  onChange,
  columns,
  onLookUpChange,
}: IUseInstructionComponentHandlersParams) => {
  const dragColumnIndex = useRef<number | null>(null)
  const [dragOverColumnIndex, setDragOverColumnIndex] = useState<number | null>(null)
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null)
  const [draftColumn, setDraftColumn] = useState<ITableColumnConfig | null>(null)

  const handleLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...instance, label: e.target.value })
    },
    [instance, onChange],
  )

  const handleAddColumn = useCallback(() => {
    const newColumn = createNewColumn(instance.columns.length)
    setEditingColumnId(newColumn.id)
    setDraftColumn({ ...newColumn })
  }, [instance.columns.length])

  const handleEditColumn = useCallback((column: ITableColumnConfig) => {
    setEditingColumnId(column.id)
    setDraftColumn({ ...column })
  }, [])

  const handleSaveColumn = useCallback(() => {
    if (!draftColumn) return
    const existsInList = instance.columns.some(col => col.id === draftColumn.id)

    if (existsInList) {
      const oldColumn = instance.columns.find(col => col.id === draftColumn.id)
      const updatedColumns = instance.columns.map(col => (col.id === draftColumn.id ? draftColumn : col))
      onChange({ ...instance, columns: updatedColumns })

      if (onLookUpChange) {
        onLookUpChange(prev => {
          const updated = { ...prev }
          if (oldColumn?.formSchemaFieldId && oldColumn.formSchemaFieldId !== draftColumn.formSchemaFieldId) {
            delete updated[oldColumn.formSchemaFieldId]
          }
          if (draftColumn.formSchemaFieldId) {
            const schemaCol = columns?.find(f => f.identifier === draftColumn.formSchemaFieldId)
            if (schemaCol) {
              updated[draftColumn.formSchemaFieldId] = {
                identifier: draftColumn.formSchemaFieldId,
                content: schemaCol.content ?? '',
                type: schemaCol.type,
              }
            }
          }
          return updated
        })
      }
    } else {
      // New column — append to list
      const newColumns = [...instance.columns, draftColumn].map((col, idx) => ({ ...col, orderNumber: idx + 1 }))
      onChange({ ...instance, columns: newColumns })

      if (onLookUpChange && draftColumn.formSchemaFieldId) {
        const fieldId = draftColumn.formSchemaFieldId
        onLookUpChange(prev => {
          const updated = { ...prev }
          const schemaCol = columns?.find(f => f.identifier === fieldId)
          if (schemaCol) {
            updated[fieldId] = {
              identifier: fieldId,
              content: schemaCol.content ?? '',
              type: schemaCol.type,
            }
          }
          return updated
        })
      }
    }

    setEditingColumnId(null)
    setDraftColumn(null)
  }, [draftColumn, instance, onChange, onLookUpChange, columns])

  const handleDraftFieldSelect = useCallback(
    (fieldId: string) => {
      const field = columns?.find(f => f.identifier === fieldId)
      if (!field || !draftColumn) return
      setDraftColumn(applyFieldToDraft(draftColumn, field))
    },
    [draftColumn, columns],
  )

  const handleDraftWidthChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!draftColumn) return
      setDraftColumn({ ...draftColumn, width: parseDraftWidth(e.target.value) })
    },
    [draftColumn],
  )

  const handleDeleteColumn = useCallback(
    (columnId: string) => {
      const columnToDelete = instance.columns.find(col => col.id === columnId)
      const updatedColumns = instance.columns
        .filter(col => col.id !== columnId)
        .map((col, idx) => ({ ...col, orderNumber: idx + 1 }))
      onChange({ ...instance, columns: updatedColumns })

      if (onLookUpChange && columnToDelete?.formSchemaFieldId) {
        const fieldId = columnToDelete.formSchemaFieldId
        onLookUpChange(prev => {
          const updated = { ...prev }
          delete updated[fieldId]
          return updated
        })
      }

      if (editingColumnId === columnId) {
        setEditingColumnId(null)
        setDraftColumn(null)
      }
    },
    [instance, onChange, editingColumnId, onLookUpChange],
  )

  const handleColumnDragStart = useCallback((e: React.DragEvent, index: number) => {
    dragColumnIndex.current = index
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const handleColumnDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    if (dragColumnIndex.current === null || dragColumnIndex.current === index) return
    setDragOverColumnIndex(index)
  }, [])

  const handleColumnDrop = useCallback(
    (e: React.DragEvent, toIndex: number) => {
      e.preventDefault()
      e.stopPropagation()
      const fromIndex = dragColumnIndex.current
      if (fromIndex === null || fromIndex === toIndex) {
        dragColumnIndex.current = null
        setDragOverColumnIndex(null)
        return
      }
      onChange({ ...instance, columns: reorderColumns(instance.columns, fromIndex, toIndex) })
      dragColumnIndex.current = null
      setDragOverColumnIndex(null)
    },
    [instance, onChange],
  )

  const handleColumnDragEnd = useCallback(() => {
    dragColumnIndex.current = null
    setDragOverColumnIndex(null)
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingColumnId(null)
    setDraftColumn(null)
  }, [])

  return {
    dragOverColumnIndex,
    editingColumnId,
    draftColumn,
    handleLabelChange,
    handleAddColumn,
    handleEditColumn,
    handleSaveColumn,
    handleDraftFieldSelect,
    handleDraftWidthChange,
    handleDeleteColumn,
    handleColumnDragStart,
    handleColumnDragOver,
    handleColumnDrop,
    handleColumnDragEnd,
    handleCancelEdit,
  }
}
